import history from 'utils/history';

import React, { useState } from 'react';
import { formatPhoneNumber } from 'react-phone-number-input';
import { useDispatch, useSelector } from 'react-redux';

import SearchBar from 'atoms/SearchBar';
import SmallLoadingIndicator from 'atoms/SmallLoadingIndicator';
import ImageDetailsCell from 'atoms/TableCell/ImageDetailsCell';
import KebabMenuCell from 'atoms/TableCell/KebabMenuCell';
import ConfirmationModal from 'components/ConfirmationModal';
import { useAllClients } from 'containers/CliniciansAndClients/selectors';
import loadIcon from 'images/load-icon.gif';
import { filterPatients } from 'utils/helpers';

import AdministrationModal from './AdministrationModal';
import OQ45DeletionMessage from './OQ45DeletionMessage';
import { createOQClient, deleteOQClient, getOQClient } from './actions';

const PatientActionsCard = (): JSX.Element => {
	const {
		isFetchingClient,
		clientHasOQAccount,
		isCreatingClient,
		isDeletingClient,
		historyURL,
		recentQuestionnaireDate,
	} = useSelector((state: RootState) => state.get('measures').toJS());

	const userRole = useSelector((state: RootState) =>
		state.get('app').get('auth').get('loggedInUser').get('roleName'),
	);

	const dispatch = useDispatch();
	const [selectedClient, setSelectedClient] = useState<ClientData | undefined>(
		undefined,
	);
	const [showModal, setShowModal] = useState(false);
	const [showMoreInfo, setShowMoreInfo] = useState(false);
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

	const allClients = useAllClients();

	const handleSearch = (value: string | number): void => {
		const client = allClients.find(
			(client: ClientData) => client.patientId.toString() === value.toString(),
		);
		setSelectedClient(client);
		if (client?.patientId) {
			dispatch(
				getOQClient({
					patientId: client.patientId?.toString(),
				}),
			);
		}
	};

	const createList = (): Array<{ label: string; value: string }> => {
		return allClients.map((client: ClientData) => {
			return {
				label: client.client?.name,
				value: client.patientId,
			};
		});
	};

	const handleCreatePatient = () => {
		if (selectedClient?.patientId) {
			dispatch(
				createOQClient({
					patientId: selectedClient.patientId.toString(),
				}),
			);
		}
	};

	const goToProfile = () => {
		const pathname = `/${userRole}/dashboard/profile/${selectedClient?.patientId}`;
		history.push({
			pathname,
		});
	};

	const handleConfirmDelete = () => {
		if (!selectedClient?.patientId) return;
		dispatch(
			deleteOQClient({ patientId: selectedClient.patientId.toString() }),
		);
		setShowDeleteConfirm(false);
	};

	const ClientCreationButton = (): JSX.Element => (
		<div>
			<button
				className="btn btn-primary primary-btn-color px-3 py-2 font-10 ml-2"
				onClick={handleCreatePatient}
				disabled={isCreatingClient}
			>
				{isCreatingClient ? (
					<img src={loadIcon} alt="Loading..." height="23px" />
				) : (
					<span>Create Client</span>
				)}
			</button>
		</div>
	);

	const ClientActionsRow = (): JSX.Element => (
		<div className="d-flex">
			<button
				onClick={() => setShowModal(true)}
				className="btn btn-primary primary-btn-color mr-2 font-10"
			>
				Administer
			</button>
			<a
				className="btn btn-primary primary-btn-color px-3 mr-2 font-10 d-inline-flex align-items-center"
				href={historyURL}
				rel="noreferrer noopener"
				target="_blank"
			>
				History
			</a>
		</div>
	);

	const kebabItems = [
		{
			id: 'show-recent',
			name: showMoreInfo
				? 'Hide most recent completed'
				: 'Show most recent completed',
			callback: () => setShowMoreInfo((prev) => !prev),
			enabled: true,
		},
		{
			id: 'delete-oq45',
			name: <span className="text-danger">Delete OQ45 account</span>,
			callback: () => setShowDeleteConfirm(true),
			enabled: Boolean(clientHasOQAccount),
		},
	];

	const ClientProfile = (): JSX.Element => {
		if (!selectedClient?.client) {
			return (
				<div className="card bg-white w-100 mx-0 pb-4 mb-2 border-0">
					<div className="text-center w-100 font-30 pt-3 uppercase">
						Search for a client
					</div>
				</div>
			);
		}

		return (
			<div className="card bg-white p-3 w-100 mx-0 mb-2">
				<div className="w-100 mx-0 justify-content-between d-flex align-items-center">
					<div className="d-flex flex-row align-items-center">
						<div className="hand mr-3" onClick={goToProfile}>
							<ImageDetailsCell
								heading={selectedClient.client.name}
								primarySubHeading={formatPhoneNumber(
									selectedClient.client.phone,
								)}
							/>
						</div>
						{showMoreInfo && (
							<span className="m-0 font-12 mr-2">
								Most recent completed: {recentQuestionnaireDate}
							</span>
						)}
						{/* Kebab sits at the left of the card: a right-aligned menu would
						    grow off the left edge of the screen. */}
						<KebabMenuCell
							noMargin
							alignRight={false}
							dropdownItems={kebabItems}
						/>
					</div>
					{isFetchingClient ? (
						<div className="d-flex">
							<SmallLoadingIndicator
								className="my-0"
								color="var(--main-color)"
							/>
						</div>
					) : clientHasOQAccount ? (
						<ClientActionsRow />
					) : (
						<ClientCreationButton />
					)}
				</div>
			</div>
		);
	};

	return (
		<>
			<div className="w-100 px-0 mb-2">
				<SearchBar
					onSearch={handleSearch}
					placeholder={'Search For Client'}
					classNames={{ container: 'mx-0' }}
					dropdownList={createList()}
					filter={filterPatients}
				/>
			</div>
			<ClientProfile />
			{selectedClient && (
				<AdministrationModal
					isOpen={showModal}
					toggle={() => setShowModal((prev) => !prev)}
					client={selectedClient}
				/>
			)}
			{showDeleteConfirm && selectedClient?.client && (
				<ConfirmationModal
					isOpen={showDeleteConfirm}
					title={`Delete OQ45 account for ${selectedClient.client.name}?`}
					message={
						<OQ45DeletionMessage
							targetName={selectedClient.client.name}
						/>
					}
					confirmText="Delete OQ45 Account"
					cancelText="Cancel"
					onConfirm={handleConfirmDelete}
					onCancel={() => setShowDeleteConfirm(false)}
					loading={isDeletingClient}
					variant="danger"
				/>
			)}
		</>
	);
};

export default PatientActionsCard;
